/**
 * Every workflow, the apps people use it in, and the Scout data it takes in.
 *
 * Grouped the way people think about them: one sport's scoring, Results for
 * every sport, and running an event. "Choose apps" on a card replaces the old
 * "Apps and commands" page; the commands themselves are in the flow diagram.
 *
 * Workflows come from the flows plugin and are installed when omnium is seeded
 * or deployed, so this page only shows them; it installs nothing.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Card, Empty, PageHead, Problem, Section } from "@/components/kit";
import { AppsSection } from "@/components/workflows/AppsSection";
import { ChooseAppsDialog } from "@/components/workflows/ChooseApps";
import { HowItFits } from "@/components/workflows/HowItFits";
import { WorkflowCard } from "@/components/workflows/WorkflowCard";
import styles from "@/components/workflows/workflows.module.css";
import {
  baseWorkflow,
  groupWorkflows,
  listApps,
  listIntegrationBriefs,
  listWorkflowDetails,
} from "@/lib/api/workflows";
import { useApi } from "@/spa/useApi";

export function WorkflowsPage() {
  const workflows = useApi(() => listWorkflowDetails(), []);
  const apps = useApi(() => listApps(), []);
  const integrations = useApi(() => listIntegrationBriefs(), [], "could not load the integrations");
  const [choosing, setChoosing] = useState<string | null>(null);
  const { hash } = useLocation();

  const rows = workflows.data;
  const appRows = apps.data;

  // A link such as /workflows#app-ringside arrives before the rows do, so
  // scroll to it once they are on the page.
  useEffect(() => {
    if (!rows || !appRows || !hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
  }, [rows, appRows, hash]);

  const reloadAll = () => {
    workflows.reload();
    apps.reload();
  };

  const chosen = rows?.find((row) => row.code === choosing);

  return (
    <>
      <PageHead
        title="Workflows"
        note="How changes get into omnium. Each workflow shows what it does, the apps people use it in, and the Scout data it takes in."
      />

      <HowItFits />

      {workflows.error ? (
        <Section title="Workflows">
          <Problem>Could not load the workflows: {workflows.error}</Problem>
        </Section>
      ) : !rows ? (
        <Section title="Workflows">
          <Card>
            <Empty title="Loading…" />
          </Card>
        </Section>
      ) : rows.length === 0 ? (
        <Section title="Workflows">
          <Card>
            <Empty
              title="No workflows yet"
              note="Workflows come from the flows plugin. They show here once omnium has installed it."
            />
          </Card>
        </Section>
      ) : (
        groupWorkflows(rows).map((group) => (
          <Section key={group.key} title={group.title} note={group.note}>
            <div className={styles.grid}>
              {group.rows.map((row) => (
                <WorkflowCard
                  key={row.code}
                  workflow={row}
                  basedOn={baseWorkflow(row, rows)}
                  integrations={integrations.data}
                  integrationsError={integrations.error}
                  onChooseApps={() => setChoosing(row.code)}
                />
              ))}
            </div>
          </Section>
        ))
      )}

      <span id="apps" />
      <AppsSection apps={appRows} error={apps.error} onChanged={reloadAll} />

      {chosen && (
        <ChooseAppsDialog
          workflow={chosen}
          apps={appRows}
          onClose={() => setChoosing(null)}
          onSaved={() => {
            setChoosing(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}
